import { useState, useEffect, ReactNode } from 'react';
import KeyboardArrowLeftIcon from '@heroicons/react/24/solid/ChevronLeftIcon';
import KeyboardArrowRightIcon from '@heroicons/react/24/solid/ChevronRightIcon';

export interface CarouselItem {
  id: string | number;
  label?: string;
  description?: string;
  content?: ReactNode;
  imgPath?: string;
  [key: string]: any; // Allow for additional custom properties
}

export interface CarouselsProps {
  items: CarouselItem[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showArrows?: boolean;
  showLabels?: boolean;
  height?: string | number;
  width?: string | number;
  itemsPerView?: number;
  gap?: number;
  renderItem?: (item: CarouselItem, index: number) => ReactNode;
}

const Carousels = ({
  items = [],
  autoPlay = false,
  autoPlayInterval = 5000,
  showArrows = true,
  showLabels = true,
  height = "auto",
  width = '100%',
  itemsPerView = 1,
  gap = 16,
  renderItem,
}: CarouselsProps) => {
  const [activeStep, setActiveStep] = useState(0);
  
  const itemWidth = `calc((100% - ${(itemsPerView - 1) * gap}px) / ${itemsPerView})`;
  const maxSteps = Math.max(1, Math.ceil(items.length / itemsPerView));

  const handleNext = () => {
    setActiveStep((prevActiveStep) => 
      prevActiveStep >= maxSteps - 1 ? 0 : prevActiveStep + 1
    );
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => 
      prevActiveStep <= 0 ? maxSteps - 1 : prevActiveStep - 1
    );
  };

  // Auto play effect
  useEffect(() => {
    if (!autoPlay) return;
    
    const timer = setInterval(() => {
      handleNext();
    }, autoPlayInterval);
    
    return () => {
      clearInterval(timer);
    };
  }, [activeStep, autoPlay, autoPlayInterval]);

  // If no items are provided, return null or a placeholder
  if (items.length === 0) {
    return <div>No items to display</div>;
  }

  const currentItem = items[activeStep * itemsPerView] || items[0];

  // Default render function for items if no custom renderer is provided
  const defaultRenderItem = (item: CarouselItem, index: number) => {
    return (
      <div className="h-full flex items-center justify-center overflow-hidden bg-white">
        {item.imgPath ? (
          <img 
            src={item.imgPath} 
            alt={item.label || `Slide ${index}`} 
            className="h-full w-full object-cover block"
          />
        ) : item.content ? (
          item.content
        ) : (
          <p>No content provided for this slide</p>
        )}
      </div>
    );
  };

  // Convert height and width to style objects
  const containerStyle = {
    width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  const transformStyle = {
    transform: `translateX(calc(-${activeStep * 100}% - ${activeStep * gap}px))`,
    gap: `${gap}px`,
  };

  return (
    <div 
      className="relative overflow-hidden rounded flex-grow" 
      style={{ width }}
    >
      {showLabels && currentItem.label && (
        <div className="flex items-center h-12 px-4 bg-gray-50">
          <h6 className="text-base font-medium">{currentItem.label}</h6>
        </div>
      )}
      
      <div className="relative overflow-hidden" style={{ height: typeof height === 'number' ? `${height}px` : height }}>
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={transformStyle}
        >
          {items.map((item, index) => (
            <div 
              key={item.id} 
              className="flex-shrink-0"
              style={{ flex: `0 0 ${itemWidth}`, width: itemWidth }}
            >
              {renderItem ? renderItem(item, index) : defaultRenderItem(item, index)}
            </div>
          ))}
        </div>
        
        {showArrows && maxSteps > 1 && (
          <>
            <button 
              onClick={handleBack} 
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full bg-white/50 hover:bg-white/80"
            >
              <KeyboardArrowLeftIcon className="h-6 w-6" />
            </button>
            <button 
              onClick={handleNext} 
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full bg-white/50 hover:bg-white/80"
            >
              <KeyboardArrowRightIcon className="h-6 w-6" />
            </button>
          </>
        )}
      </div>
      
      {showLabels && currentItem.description && (
        <div className="p-4 bg-gray-50">
          <p>{currentItem.description}</p>
        </div>
      )}
    </div>
  );
};

export default Carousels;